import { frequencyTypes } from "@/constants";

/**
 * How often each frequency falls in a year. Every conversion below is derived from this one table, so the
 * relationship between the frequencies is described in a single place rather than as a scattering of 52s,
 * 26s and 12s spread across the calculations that need them.
 */
const PERIODS_PER_YEAR: Record<string, number> = {
  [frequencyTypes.Weekly]: 52,
  [frequencyTypes.Fortnightly]: 26,
  [frequencyTypes.Monthly]: 12,
  [frequencyTypes.Annually]: 1,
};

const MONTHS_PER_YEAR = 12;

/**
 * The same table keyed for lookup. Repayment frequencies are stored capitalised ("Monthly") while fee
 * frequencies are stored lowercased ("monthly"), and both reach these helpers, so the match is made case
 * insensitively rather than each caller remembering which casing its own records use.
 */
const PERIODS_PER_YEAR_BY_KEY: Record<string, number> = Object.fromEntries(
  Object.entries(PERIODS_PER_YEAR).map(([frequency, periods]) => [frequency.toLowerCase(), periods]),
);

/** Periods a year for a stored frequency, or undefined when it is not one of the known frequencies. */
const getPeriodsPerYear = (frequency?: string | null): number | undefined =>
  frequency ? PERIODS_PER_YEAR_BY_KEY[frequency.toLowerCase()] : undefined;

/**
 * An amount charged at the given frequency, as a monthly amount. An unrecognised frequency falls back to
 * `fallbackFactor`, which defaults to 1 — the amount is taken to be monthly already.
 */
const toMonthlyAmount = (amount: number, frequency?: string | null, fallbackFactor = 1): number => {
  const periodsPerYear = getPeriodsPerYear(frequency);
  return amount * (periodsPerYear === undefined ? fallbackFactor : periodsPerYear / MONTHS_PER_YEAR);
};

/**
 * A monthly amount expressed at the given frequency — the inverse of `toMonthlyAmount`. An unrecognised
 * frequency leaves the amount as it is, since there is no period to spread it over.
 */
const fromMonthlyAmount = (monthlyAmount: number, frequency?: string | null): number => {
  const periodsPerYear = getPeriodsPerYear(frequency);
  if (periodsPerYear === undefined) return monthlyAmount;

  return (monthlyAmount * MONTHS_PER_YEAR) / periodsPerYear;
};

export { toMonthlyAmount, fromMonthlyAmount };
